import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9th4l5ca23r0tr1aj3g';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 艳姆1—6系列解析站',
  description:
    '了解艳姆系列解析团队：专注动画深度内容创作与分享，致力于为观众提供最全面的艳姆1—6系列介绍与解析。',
  url: typeof window !== 'undefined' ? window.location.href : '',
  about: {
    '@type': 'Organization',
    name: '艳姆系列解析团队',
    description:
      '专注于动画系列深度内容解析与分享的创作团队',
  },
};

function ContactForm() {
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus(null);

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setStatus({ type: 'success', message: '消息已发送，我们会尽快回复！' });
      form.reset();
      return;
    }

    const textarea = form.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
    if (textarea && textarea.value.length > 500) {
      setStatus({ type: 'error', message: '消息内容不能超过500个字符' });
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotEl) {
        formData.delete('website_alt');
      }

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // raw text response
      }

      if (response.ok && (parsed as { code?: string }).code === 'OK') {
        setStatus({ type: 'success', message: '消息已发送，我们会尽快回复！' });
        form.reset();
      } else {
        const serverMsg =
          ((parsed as { meta?: { message?: string } }).meta?.message) ||
          ((parsed as { message?: string }).message) ||
          responseText ||
          '发送失败，请稍后再试';
        setStatus({ type: 'error', message: String(serverMsg) });
      }
    } catch {
      setStatus({ type: 'error', message: '网络错误，请检查连接后重试' });
    }
  };

  return (
    <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
            姓名
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            className="w-full px-3.5 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="您的姓名"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
            邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            className="w-full px-3.5 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            placeholder="your@email.com"
          />
        </div>
      </div>
      <div>
        <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
          主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          required
          className="w-full px-3.5 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
          placeholder="消息主题"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
          消息内容 <span className="text-foreground-400">（最多500字）</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          maxLength={500}
          required
          className="w-full px-3.5 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
          placeholder="请输入您想说的话..."
        ></textarea>
      </div>

      {status && (
        <div
          className={`text-sm px-4 py-2.5 rounded-lg ${
            status.type === 'success'
              ? 'bg-accent-50 text-accent-800 border border-accent-200'
              : 'bg-primary-50 text-primary-800 border border-primary-200'
          }`}
        >
          {status.message}
        </div>
      )}

      <button
        type="submit"
        className="px-6 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
      >
        发送消息
      </button>
    </form>
  );
}

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[{ label: '首页', href: '/' }, { label: '关于我们' }]}
          />

          <header className="mb-12 text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              关于我们
            </h1>
            <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-sm">
              艳姆系列解析团队致力于为动画爱好者提供最全面、最深入的系列内容解析
            </p>
          </header>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              品牌介绍
            </h2>
            <div className="space-y-4">
              <p className="text-foreground-700 leading-relaxed">
                艳姆系列解析站创立于2026年，是一个专注于动画系列作品深度内容解析与分享的平台。我们汇聚了一群热爱动画、具有专业分析能力的创作者，致力于为观众提供高质量的系列介绍、剧情分析和观看指引。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                我们的核心团队由资深动画评论人、影视编剧和视觉设计师组成。每一位成员都拥有多年的动画内容创作经验，对动画艺术的叙事结构、角色塑造和视觉语言有着深刻的理解。正是这种专业的背景和对动画的热爱，驱使我们不断产出有价值的内容。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                在艳姆系列解析站，我们秉持<strong>"深度内容，用心呈现"</strong>的理念。我们不满足于表面的剧情复述，而是深入挖掘每一个镜头背后的叙事意图、每一段配乐承载的情感重量、每一位角色蕴含的人性思考。我们相信，好的动画作品值得被认真对待，好的内容解析能够帮助观众获得更深层次的观看体验。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                目前，我们的内容已覆盖艳姆1—6全系列的剧情解析、人物分析、艺术风格解读、配乐鉴赏、幕后故事和观看指引等多个维度。未来，我们将继续扩展内容领域，为观众带来更多有价值的动画解析内容。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              团队声誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mx-auto mb-4">
                  <i className="ri-user-star-line text-2xl"></i>
                </div>
                <div className="text-3xl font-heading font-bold text-foreground-950 mb-1">
                  50万+
                </div>
                <p className="text-sm text-foreground-600">累计读者</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mx-auto mb-4">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <div className="text-3xl font-heading font-bold text-foreground-950 mb-1">
                  200+篇
                </div>
                <p className="text-sm text-foreground-600">原创深度文章</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mx-auto mb-4">
                  <i className="ri-thumb-up-line text-2xl"></i>
                </div>
                <div className="text-3xl font-heading font-bold text-foreground-950 mb-1">
                  98%
                </div>
                <p className="text-sm text-foreground-600">读者好评率</p>
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              读者评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  name: '动漫爱好者小王',
                  text: '这是我找到的最全面的艳姆系列解析！每一集的剧情分析都写得非常透彻，让我对作品有了全新的理解。',
                  date: '2026-08-05',
                },
                {
                  name: '影视研究者L',
                  text: '团队的专业素养令人钦佩。从叙事结构到视觉语言的分析都有理有据，是难得的高质量动画解析内容。',
                  date: '2026-08-03',
                },
                {
                  name: '忠实读者张先生',
                  text: '关注这个站点已经有一段时间了，每次更新都不会错过。文章质量一直很稳定，深度和可读性都很出色。',
                  date: '2026-07-28',
                },
                {
                  name: '动画专业学生小C',
                  text: '作为一个学动画的学生，这里的分析对我的专业学习很有帮助。特别是关于色彩叙事和动作设计的解读，给了我很多启发。',
                  date: '2026-07-25',
                },
              ].map((review, index) => (
                <div
                  key={index}
                  className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">
                    &ldquo;{review.text}&rdquo;
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-foreground-900">
                      {review.name}
                    </span>
                    <span className="text-xs text-foreground-500">{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              <div className="md:col-span-2 space-y-5">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">联系方式</h3>
                  <div className="space-y-3 text-sm text-foreground-600">
                    <p className="flex items-center gap-2">
                      <i className="ri-mail-line text-primary-500"></i>
                      contact@kioyxr.cn
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-map-pin-line text-primary-500"></i>
                      在线创作团队，服务全球华语观众
                    </p>
                    <p className="flex items-center gap-2">
                      <i className="ri-time-line text-primary-500"></i>
                      工作日 9:00 - 18:00
                    </p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-900 mb-2">关注我们</h3>
                  <div className="flex items-center gap-3">
                    <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow" aria-label="微博">
                      <i className="ri-weibo-line text-lg"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow" aria-label="微信">
                      <i className="ri-wechat-line text-lg"></i>
                    </a>
                    <a href="#" className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors" rel="nofollow" aria-label="B站">
                      <i className="ri-bilibili-line text-lg"></i>
                    </a>
                  </div>
                </div>
              </div>

              <div className="md:col-span-3">
                <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <h3 className="text-base font-heading font-semibold text-foreground-950 mb-4">
                    发送消息
                  </h3>
                  <ContactForm />
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}